import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Icon } from "@amzn/meridian";
import Link from "@amzn/meridian/link";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import { ProductClassification, ProductAttributes, WasteProfilePathway, ClassifyWasteProfileResponse } from "src/types/classificationTypes";
import { ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import PillButton from "../../UI/PillButton";
import WasteProfileCard from "./WasteProfileCard";
import AiResearchCard from "./AiResearchCard";
import ThemedTag from "src/components/pages/configurationDrivenInspections/inspectionReview/ThemedTag";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import "./classification.scss";

/** Small helper — renders one label/value row in the Product Attributes table */
const AttrRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Column>
    <Row alignmentHorizontal="justify">
      <Text alignment="start">{label}</Text>
      <Text alignment="end">{value}</Text>
    </Row>
    <Divider />
  </Column>
);

const formatDimensions = (attrs: ProductAttributes): string => {
  if (!attrs?.dimensions) return "";
  const { width, length, height } = attrs.dimensions;
  const parts = [width?.value, length?.value, height?.value].filter(Boolean);
  if (parts.length === 0) return "";
  return `${parts.map((v) => v?.toFixed(2)).join(" x ")} ${height?.unit || "in"}`;
};

const formatWeight = (attrs: ProductAttributes): string => {
  if (!attrs?.weight) return "";
  return `${attrs.weight.value.toFixed(3)} ${attrs.weight.unit === "pounds" ? "lb" : attrs.weight.unit}`;
};

const formatHazmat = (attrs: ProductAttributes): string => {
  if (!attrs?.hazmat_statement?.length) return "";
  return attrs.hazmat_statement.map((h) => h.value.toUpperCase()).join(" / ");
};

// Minimum px distance to register as a swipe (prevents accidental taps)
const SWIPE_THRESHOLD_PX = 30;

interface ResultsPageLocationState {
  data: ProductClassification;
  itemFlowContext?: ItemFlowContext;
  isMissingReport?: boolean;
  isFeedbackCaptured?: boolean;
  savedWasteProfileData?: ClassifyWasteProfileResponse;
}

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, itemFlowContext, isMissingReport, isFeedbackCaptured, savedWasteProfileData } =
    (location.state as ResultsPageLocationState) ?? {};

  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [isWasteProfileLoading, setIsWasteProfileLoading] = useState(true);
  const [pathway, setPathway] = useState<WasteProfilePathway | null>(null);
  const [classificationId, setClassificationId] = useState("");
  const touchStartX = useRef<number>(0);

  if (!data) {
    return (
      <Column
        width="100%"
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
      >
        <Text>No classification data available.</Text>
        <PillButton text="Go Back" color="secondary" onClick={() => navigate("/home")} />
      </Column>
    );
  }

  const {
    waste_profiles: wasteProfiles = [],
    donation_categories: donationCategories = [],
    sds_list: sdsList = [],
    product_attributes: productAttributes,
    images = [],
  } = data;

  const isIneligibleInTote =
    itemFlowContext?.type === "tote" &&
    itemFlowContext.items.some(
      (item) => item.donationEligibilityInformation?.isEligibleForDonation === false,
    );
  const isEligibleForDonation =
    data.donation_eligibility_information?.isEligibleForDonation === true && !isIneligibleInTote;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > SWIPE_THRESHOLD_PX && activeImageIdx < images.length - 1) {
      setActiveImageIdx(activeImageIdx + 1);
    } else if (diff < -SWIPE_THRESHOLD_PX && activeImageIdx > 0) {
      setActiveImageIdx(activeImageIdx - 1);
    }
  };

  const resolveItemFlowContext = (): ItemFlowContext => {
    const recommendedWasteProfileIds = savedWasteProfileData?.recommendedWasteProfilesIds;
    if (itemFlowContext?.type === "toteExtraItem") {
      // Report extra item flow — complete the context with the scanned product
      return {
        ...itemFlowContext,
        productClassification: data,
        classificationId,
        recommendedWasteProfileIds,
      };
    }
    if (itemFlowContext?.type === "tote") {
      // Tote flow — context is already complete, forward as-is
      return itemFlowContext;
    }
    // Plain classification flow — no prior context, build fresh
    return {
      type: "classification",
      productClassification: data,
      classificationId,
      recommendedWasteProfileIds,
    };
  };

  const handleAddToContainer = (usingFor: ContainerUsingFor) => {
    const wasteProfileIds =
      savedWasteProfileData?.recommendedWasteProfilesIds ?? [];
    const donationCategoryIds = (donationCategories ?? []).map((d) => d.id);

    navigate("/tote/chooseContainer", {
      state: {
        itemFlowContext: resolveItemFlowContext(),
        wasteProfileIds: usingFor === "WASTE" ? wasteProfileIds : [],
        donationCategoryIds: usingFor === "DONATION" ? donationCategoryIds : [],
        usingFor,
      },
    });
  };

  const handleViewSdsResults = (result: ClassifyWasteProfileResponse) => {
    navigate("/classification/sdsDataSheet", {
      state: {
        result,
        asin: data.asin,
        itemName: data.name,
        isEligibleForDonation,
        itemFlowContext: resolveItemFlowContext(),
        donationCategoryIds: (donationCategories ?? []).map((d) => d.id),
      },
    });
  };

  const handleReportMissing = () => {
    if (!itemFlowContext || itemFlowContext.type !== "tote") return;
    navigate("/tote/confirmQuantity", {
      state: {
        itemFlowContext,
        isMissingReport: true,
      },
    });
  };

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="500 500 600">
      {/* Top bar: back arrow + ASIN only (no site) */}
      <Row alignmentVertical="center" spacingInset="400 none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h100">ASIN {data.asin}</Text>
      </Row>

      {/* Product name + ASIN */}
      <Column spacing="300" spacingInset="100 400">
        {isEligibleForDonation && (
          <Row>
            <ThemedTag text="Eligible for donation" type="success" iconTokens={checkLargeTokens} />
          </Row>
        )}
        <Text type="h300">{data.name}</Text>
        <Text type="h100" color="secondary">
          ASIN: {data.asin}
        </Text>
      </Column>

      {/* Product images */}
      {images.length > 0 && (
        <Column alignmentHorizontal="center" spacing="400" spacingInset="100">
          <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <img
              style={{ width: "200px", height: "200px", objectFit: "contain" }}
              src={images[activeImageIdx]?.url}
              alt={data.name || "Product"}
            />
          </div>
          {/* Dot indicators */}
          {images.length > 1 && (
            <Row alignmentHorizontal="center" spacing="200">
              {images.map((_, idx) => (
                <div
                  key={idx}
                  className={`asin-dot ${idx === activeImageIdx ? "asin-dot--active" : ""}`}
                  onClick={() => setActiveImageIdx(idx)}
                />
              ))}
            </Row>
          )}
        </Column>
      )}

      {/* Waste Profiles */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">
          WASTE PROFILES
        </Text>
        <WasteProfileCard
          asin={data.asin}
          itemName={data.name}
          onLoadingChange={setIsWasteProfileLoading}
          onPathwayChange={setPathway}
          onClassificationIdChange={setClassificationId}
          isFeedbackCaptured={isFeedbackCaptured}
          savedWasteProfileData={savedWasteProfileData}
        />
      </Column>

      {/* Donation Categories */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">
          DONATION CATEGORIES
        </Text>
        {donationCategories.length > 0 && (
          <Column className="home-card" spacing="200" spacingInset="400 500">
            <Text tag="ul">
              {donationCategories.map((dc, idx) => (
                <Text key={idx} tag="li" color="secondary">
                  {dc.name}
                </Text>
              ))}
            </Text>
          </Column>
        )}
      </Column>

      {/* Safety Data Sheets */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">
          SAFETY DATA SHEETS
        </Text>
        <Column className="home-card" spacing="200" spacingInset="400 500">
          {sdsList.length > 0 ? (
            sdsList.map((sds, idx) => (
              <Link key={idx} href={sds.url} target="_blank" rel="noopener noreferrer">
                <Row alignmentVertical="center" spacing="400">
                  <Icon color="primary" tokens={documentTokens} />
                  <Column spacing="none">
                    <Text>SDS Document {sds.language ? sds.language.toUpperCase() : ""}</Text>
                    <Text color="secondary" type="b200">
                      Tap to view
                    </Text>
                  </Column>
                </Row>
              </Link>
            ))
          ) : (
            <Text color="secondary" type="h100">
              SDS sheet is not available.
            </Text>
          )}
        </Column>
      </Column>

      {/* AI-Assisted Research (hidden for golden ASINs, where SDS attributes are ignored) */}
      {pathway && pathway !== WasteProfilePathway.GOLDEN_ASIN && (
        <Column spacing="300" spacingInset="200">
          <AiResearchCard
            asin={data.asin}
            itemName={data.name}
            classificationId={classificationId}
            onViewResults={handleViewSdsResults}
          />
        </Column>
      )}

      {/* Product Attributes */}
      <Column spacingInset="200" spacing="300">
        <Text type="h100" color="secondary">
          PRODUCT ATTRIBUTES
        </Text>
        {productAttributes && (
          <Column className="home-card" spacingInset="400">
            {productAttributes.weight && (
              <AttrRow label="Weight" value={formatWeight(productAttributes)} />
            )}
            {productAttributes.dimensions && (
              <AttrRow label="Dimensions" value={formatDimensions(productAttributes)} />
            )}
            {productAttributes.hazmat_state && (
              <AttrRow
                label="Hazmat State"
                value={
                  productAttributes.hazmat_state.charAt(0).toUpperCase() +
                  productAttributes.hazmat_state.slice(1)
                }
              />
            )}
            {productAttributes.hazmat_statement &&
              productAttributes.hazmat_statement.length > 0 && (
                <AttrRow label="Hazmat Statement" value={formatHazmat(productAttributes)} />
              )}
            {productAttributes.product_group && (
              <AttrRow label="Product Group" value={productAttributes.product_group} />
            )}
          </Column>
        )}
      </Column>

      <Column className="asin-sticky-footer" spacing="300">
        {isMissingReport ? (
          <PillButton text="REPORT AS MISSING" color="primary" onClick={handleReportMissing} />
        ) : (
          <Row spacing="300" alignmentHorizontal="center">
            <PillButton
              text="DONATE"
              color="outline"
              onClick={() => handleAddToContainer("DONATION")}
              disabled={isEligibleForDonation !== true || isWasteProfileLoading}
            />
            <PillButton
              text="ADD TO CONTAINER"
              color="success"
              onClick={() => handleAddToContainer("WASTE")}
              disabled={isWasteProfileLoading}
            />
          </Row>
        )}
      </Column>
    </Column>
  );
};

export default ResultsPage;
